import React, { useState, useEffect } from 'react';
import { Card, Badge, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faTasks,
  faRocket,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faBrain,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface LeadScore {
  score: number;
  quality_level: 'high' | 'medium' | 'basic' | 'low' | 'pending';
  conversion_potential: number;
  lead_value: number;
  evaluation_date: string;
}

interface NextTask {
  id: string;
  description: string;
  type: string;
  priority: number;
  due_date: string;
  status: string;
  created_by: string;
  created_at: string;
  urgency?: 'critical' | 'high' | 'medium' | 'low';
  is_overdue?: boolean;
  hours_until_due?: number;
}

// Updated for Universal System Architecture alignment
interface WorkflowAlert {
  workflow_id: string;
  workflow_type: string;
  workflow_stage: string;
  due_date: string;
  hours_until_due: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  alert_type: 'due_soon' | 'overdue';
  message: string;
  actions: Array<{
    action: string;
    label: string;
    icon: string;
  }>;
  metadata: {
    created_at: string;
    workflow_progress: number;
    template_name: string;
  };
}

interface UniversalWorkflowPanelProps {
  className?: string;
  leadId: string;
  refreshTrigger?: number;
  onProactiveAlerts?: (alerts: WorkflowAlert[]) => void;
}

const UniversalWorkflowPanel: React.FC<UniversalWorkflowPanelProps> = ({
  className,
  leadId,
  refreshTrigger = 0,
  onProactiveAlerts
}) => {
  const [loading, setLoading] = useState(true);
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);
  const [nextTasks, setNextTasks] = useState<NextTask[]>([]);
  const [error, setError] = useState<string>('');

  // Load data from Universal System
  const loadUniversalData = async (forceRefresh = false) => {
    if (!leadId) return;

    setLoading(true);
    setError('');

    try {
      console.log('🔄 [UNIVERSAL SYSTEM] Loading lead evaluation and tasks for:', leadId);

      // Check if we already have a recent evaluation in sessionStorage
      const cacheKey = `lead_evaluation_${leadId}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      let evaluationResponse;
      
      // Force refresh if requested or if refreshTrigger changed
      if (!forceRefresh && cachedData && refreshTrigger === 0) {
        const cached = JSON.parse(cachedData);
        const cacheAge = Date.now() - cached.timestamp;
        const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
        
        if (cacheAge < CACHE_DURATION) {
          console.log('📦 [CACHE HIT] Using cached evaluation from', new Date(cached.timestamp).toLocaleTimeString());
          evaluationResponse = { data: cached.response };
        } else {
          console.log('⏰ [CACHE EXPIRED] Cache is older than 30 minutes, fetching fresh evaluation');
          evaluationResponse = await triggerFreshEvaluation();
        }
      } else {
        console.log('🆕 [NO CACHE] Fetching fresh evaluation');
        evaluationResponse = await triggerFreshEvaluation();
      }
      
      async function triggerFreshEvaluation() {
        try {
          const response = await axiosInstance.post(`/leads/${leadId}/trigger-evaluation`, {
            evaluationType: 'page_load_proactive'
          }, {
            timeout: 25000 // 25 second timeout to avoid 504
          });
          
          // Cache the successful response
          if (response.data.success) {
            sessionStorage.setItem(cacheKey, JSON.stringify({
              timestamp: Date.now(),
              response: response.data
            }));
          }
          
          return response;
        } catch (error: any) {
          // Handle 504 timeout specifically
          if (error.response?.status === 504 || error.code === 'ECONNABORTED') {
            console.warn('⚠️ Universal evaluation timed out, using fallback data');
            
            // Return mock evaluation for demo
            return {
              data: {
                success: true,
                evaluation_data: {
                  evaluation: {
                    lead_score: 85,
                    engagement_level: 'High',
                    recommended_actions: ['Follow up with property matches', 'Schedule consultation'],
                    behavioral_insights: {
                      search_intensity: 'Active',
                      price_sensitivity: 'Moderate',
                      timeline: '30-60 days'
                    }
                  }
                }
              }
            };
          }
          throw error;
        }
      }
      
      if (evaluationResponse.data.success) {
        // Extract evaluation data from the correct nested path
        const fullResponse = evaluationResponse.data;
        const evaluationData = fullResponse.evaluation_data?.evaluation;
        
        console.log('📊 [EVALUATION RESPONSE] Full response:', fullResponse);
        console.log('📊 [EVALUATION DATA] Evaluation data:', evaluationData);
        
        // Validate evaluation data has required fields
        if (evaluationData && typeof evaluationData === 'object') {
          // Fetch enhanced behavioral data from the lead evaluation endpoint
          let behavioralData = null;
          let hasEnhancedData = false;
          
          try {
            // Get the full lead evaluation which includes behavioral data
            console.log('🔍 [FETCHING BEHAVIORAL DATA] Getting lead evaluation for VIP check...');
            const behavioralResponse = await axiosInstance.get(`/leads/${leadId}/evaluation`);
            
            if (behavioralResponse.data.success && behavioralResponse.data.evaluation) {
              const fullBehavioralEval = behavioralResponse.data.evaluation;
              behavioralData = fullBehavioralEval.behavioral_data;
              console.log('🔍 [BEHAVIORAL DATA] Found:', behavioralData);
              console.log('📊 [BEHAVIORAL SCORE] Score from behavioral analysis:', fullBehavioralEval.score);
              
              // Store the full behavioral evaluation for comprehensive scoring
              evaluationData.behavioralEvaluation = fullBehavioralEval;
              
              hasEnhancedData = behavioralData?.vip_status?.is_vip || 
                               behavioralData?.property_history?.transaction_count > 0;
            }
          } catch (error) {
            console.log('⚠️ [BEHAVIORAL DATA] Could not fetch enhanced data:', error);
          }
          
          // Use the best available score - prioritize behavioral analysis
          let enhancedScore = evaluationData.confidence_score || evaluationData.score || 0;
          let scoreSource = 'workflow';
          
          // If we have behavioral evaluation data, use that score instead
          if (evaluationData.behavioralEvaluation) {
            enhancedScore = evaluationData.behavioralEvaluation.score || enhancedScore;
            scoreSource = 'behavioral_analysis';
            console.log('🎯 [SCORE] Using behavioral analysis score:', enhancedScore);
          }
          
          console.log(`📊 [FINAL SCORE] Score: ${enhancedScore}, Source: ${scoreSource}, VIP: ${hasEnhancedData}`);
          
          // Map the actual API response to the expected format - use behavioral data when available
          const behavioralEval = evaluationData.behavioralEvaluation;
          const validatedScore: LeadScore = {
            score: enhancedScore,
            quality_level: behavioralEval?.quality_level || 
                          (hasEnhancedData ? 'high' : mapPriorityToQualityLevel(evaluationData.priority_level || evaluationData.quality_level)) as LeadScore['quality_level'],
            conversion_potential: behavioralEval?.conversion_potential || 
                                 (hasEnhancedData ? 0.8 : mapConversionPotential(evaluationData.conversion_potential)),
            lead_value: behavioralEval?.lead_value || 
                       evaluationData.lead_value || 
                       (hasEnhancedData ? 50000 : estimateLeadValue(enhancedScore)),
            evaluation_date: behavioralEval?.evaluation_date || evaluationData.evaluation_date || new Date().toISOString()
          };
          setLeadScore(validatedScore);
          console.log('📊 [LEAD SCORE] Fresh evaluation triggered:', validatedScore);
          console.log('📊 [LEAD SCORE] Evaluation data:', evaluationData);
          
          // Extract next_actions from evaluation data and merge with behavioral recommendations
          let allActions: any[] = [];
          
          // First, add behavioral recommendations if available (priority based on score)
          if (behavioralData?.recommendations && Array.isArray(behavioralData.recommendations)) {
            const isVIP = enhancedScore >= 80; // Score-based VIP determination
            console.log(`📊 [BEHAVIORAL RECOMMENDATIONS] Score: ${enhancedScore}, VIP: ${isVIP}`);
            
            const behavioralTasks = behavioralData.recommendations.map((rec: string, index: number) => ({
              id: `behavioral_rec_${index}`,
              task_name: rec,
              description: rec,
              priority: enhancedScore >= 80 ? 3 : enhancedScore >= 60 ? 2 : 1, // Score-based priority
              urgency: enhancedScore >= 80 ? 'high' : enhancedScore >= 60 ? 'medium' : 'low',
              is_overdue: false,
              hours_until_due: enhancedScore >= 80 ? 24 : 48, // VIP gets shorter deadlines
              created_at: new Date().toISOString(),
              source: 'behavioral_analysis',
              type: isVIP ? 'vip_action' : 'qualified_action'
            }));
            allActions = [...behavioralTasks];
          }
          
          // Then add workflow next actions
          if (evaluationData.next_actions && Array.isArray(evaluationData.next_actions)) {
            console.log('📋 [WORKFLOW ACTIONS] Found:', evaluationData.next_actions);
            
            const workflowTasks = evaluationData.next_actions.map((action: string, index: number) => ({
              id: `eval_action_${index}`,
              task_name: action,
              description: action,
              priority: enhancedScore >= 80 ? 3 : enhancedScore >= 50 ? 2 : 1,
              urgency: enhancedScore >= 80 ? 'high' : enhancedScore >= 50 ? 'medium' : 'low',
              is_overdue: false,
              hours_until_due: 48,
              created_at: new Date().toISOString(),
              source: 'workflow_evaluation',
              type: 'workflow_action'
            }));
            
            allActions = [...allActions, ...workflowTasks];
          }
          
          if (allActions.length > 0) {
            setNextTasks(allActions);
            console.log('✅ [MERGED ACTIONS] Combined VIP + Workflow tasks:', allActions);
            
            // Skip the workflow-tasks API call since we have tasks from evaluation
            return;
          }
        } else {
          console.warn('⚠️ [LEAD SCORE] Invalid evaluation data received:', evaluationData);
          setLeadScore(null);
        }
      }

      // Try to get workflow tasks (these are actual created tasks in the system)
      try {
        const tasksResponse = await axiosInstance.get(`/leads/${leadId}/workflow-tasks`);
        
        if (tasksResponse.data.success && tasksResponse.data.tasks && tasksResponse.data.tasks.length > 0) {
          const workflowTasks = tasksResponse.data.tasks || [];
          console.log('📋 [WORKFLOW TASKS] Found existing workflow tasks:', workflowTasks);
          
          // Add proactive intelligence - analyze task urgency
          const tasksWithUrgency = workflowTasks.map((task: any) => {
          const now = new Date();
          let urgency: 'critical' | 'high' | 'medium' | 'low' = 'low';
          let is_overdue = false;
          let hours_until_due = 0;
          
          // Use due_date if available, otherwise treat as low priority
          if (task.due_date) {
            const dueDate = new Date(task.due_date);
            const hoursDiff = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
            hours_until_due = Math.abs(hoursDiff);
            
            // Proactive urgency calculation based on due date
            if (hoursDiff < 0) {
              urgency = 'critical';
              is_overdue = true;
            } else if (hoursDiff <= 2) {
              urgency = 'high';
            } else if (hoursDiff <= 8) {
              urgency = 'medium';
            }
          } else {
            // No due date - calculate based on creation time for context
            const createdDate = new Date(task.created_at);
            const hoursSinceCreated = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
            hours_until_due = hoursSinceCreated;
            
            // Tasks without due dates are medium priority if created recently
            if (hoursSinceCreated <= 24) {
              urgency = 'medium';
            }
          }
          
          return {
            ...task,
            urgency,
            is_overdue,
            hours_until_due
          };
        });
        
        setNextTasks(tasksWithUrgency);
        console.log('📋 [PROACTIVE TASKS] Loaded tasks with urgency analysis:', tasksWithUrgency);
        
        // 🚀 PROACTIVE CHATBOT INTEGRATION: Generate alerts for existing chatbot system
        const urgentTasks = tasksWithUrgency.filter((task: NextTask) => 
          task.urgency === 'critical' || task.urgency === 'high'
        );
        
        if (urgentTasks.length > 0 && onProactiveAlerts) {
          console.log(`📢 [PROACTIVE] Found ${urgentTasks.length} urgent tasks - sending to chatbot system`);
          const workflowAlerts = convertTasksToWorkflowAlerts(urgentTasks);
          onProactiveAlerts(workflowAlerts);
        }
        
        // Merge workflow tasks with evaluation tasks (if both exist)
        if (nextTasks.length > 0) {
          // We already have evaluation tasks, merge with workflow tasks
          const mergedTasks = [...nextTasks, ...tasksWithUrgency];
          // Remove duplicates based on task name
          const uniqueTasks = mergedTasks.filter((task, index, self) =>
            index === self.findIndex((t) => t.task_name === task.task_name)
          );
          setNextTasks(uniqueTasks);
          console.log('✅ [TASKS MERGED] Combined evaluation and workflow tasks:', uniqueTasks);
        } else {
          // Only workflow tasks exist
          setNextTasks(tasksWithUrgency);
        }
      }
    } catch (taskError: any) {
      console.log('⚠️ [WORKFLOW TASKS] Could not fetch workflow tasks:', taskError.message);
      // Not critical - we still have evaluation tasks
    }
    
    // Only trigger workflow orchestrator if this is a fresh evaluation (not from cache)
    const orchestratorCacheKey = `workflow_orchestrator_${leadId}`;
    const lastOrchestratorRun = sessionStorage.getItem(orchestratorCacheKey);
    const shouldRunOrchestrator = !lastOrchestratorRun || 
      (Date.now() - parseInt(lastOrchestratorRun)) > (60 * 60 * 1000); // 1 hour
    
    if (shouldRunOrchestrator && !cachedData) {
      try {
        console.log('🤖 [WORKFLOW ORCHESTRATOR] Triggering workflow creation from fresh evaluation...');
        const orchestratorResponse = await axiosInstance.get(`/workflow/lead-progress/${leadId}`);
        
        if (orchestratorResponse.data.success) {
          console.log('✅ [WORKFLOW ORCHESTRATOR] Workflow tasks created successfully');
          // Mark that we've run the orchestrator for this lead
          sessionStorage.setItem(orchestratorCacheKey, Date.now().toString());
          
          // Wait a moment then fetch the newly created tasks
          setTimeout(async () => {
            try {
              const newTasksResponse = await axiosInstance.get(`/leads/${leadId}/workflow-tasks`);
              if (newTasksResponse.data.success && newTasksResponse.data.tasks) {
                console.log('📋 [NEW TASKS] Fetched newly created workflow tasks');
                // Update tasks but don't trigger full reload
              }
            } catch (e: any) {
              console.log('Could not fetch new tasks:', e.message);
            }
          }, 2000);
        }
      } catch (orchError: any) {
        console.log('⚠️ [WORKFLOW ORCHESTRATOR] Could not trigger workflow creation:', orchError.message);
        // Not critical - evaluation still provides next_actions
      }
    } else {
      console.log('⏭️ [WORKFLOW ORCHESTRATOR] Skipping - already ran recently or using cached evaluation');
    }

    } catch (error: any) {
      console.error('❌ [UNIVERSAL SYSTEM] Failed to load data:', error);
      setError(error.response?.data?.message || 'Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or refreshTrigger changes
  useEffect(() => {
    if (leadId) {
      loadUniversalData();
    }
  }, [leadId, refreshTrigger]);

  // Helper functions to map API response to expected format
  const mapPriorityToQualityLevel = (priority: string | undefined): string => {
    const mapping: Record<string, string> = {
      'Hot': 'high',
      'Warm': 'medium',
      'Cold': 'low',
      'high': 'high',
      'medium': 'medium',
      'low': 'low',
      'basic': 'basic'
    };
    return mapping[priority || ''] || 'pending';
  };

  const mapConversionPotential = (potential: string | number | undefined): number => {
    if (typeof potential === 'number') return potential;
    const mapping: Record<string, number> = {
      'Hot': 0.8,
      'Warm': 0.5,
      'Cold': 0.2,
      'high': 0.8,
      'medium': 0.5,
      'low': 0.2
    };
    return mapping[potential || ''] || 0.1;
  };

  const estimateLeadValue = (confidenceScore: number | undefined): number => {
    // Estimate lead value based on confidence score
    const score = confidenceScore || 0;
    if (score >= 80) return 50000;
    if (score >= 60) return 25000;
    if (score >= 40) return 10000;
    if (score >= 20) return 5000;
    return 1000;
  };

  // Get quality color based on score
  const getQualityColor = (quality: string | undefined) => {
    switch (quality) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'basic': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  // Get priority color for tasks
  const getPriorityColor = (priority: number) => {
    if (priority >= 3) return 'danger';
    if (priority >= 2) return 'warning';
    return 'info';
  };

  // Get urgency color for proactive alerts
  const getUrgencyColor = (urgency?: string) => {
    switch (urgency) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  // Get urgency icon for proactive display
  const getUrgencyIcon = (urgency?: string, isOverdue?: boolean) => {
    if (isOverdue) return '🚨';
    switch (urgency) {
      case 'critical': return '🔴';
      case 'high': return '⚠️';
      case 'medium': return '📅';
      case 'low': return '🔔';
      default: return '📋';
    }
  };

  // Handle one-click actions for proactive system
  const handleTaskAction = async (taskId: string, action: 'complete' | 'snooze') => {
    try {
      console.log(`🎯 [PROACTIVE ACTION] ${action} task:`, taskId);
      
      if (action === 'complete') {
        // Mark task as completed in LeadTasks
        await axiosInstance.patch(`/leads/${leadId}/tasks/${taskId}`, {
          is_completed: true
        });
        toast.success('✅ Task completed!');
        
        // Refresh tasks to reflect changes
        loadUniversalData();
      }
      
    } catch (error) {
      console.error('❌ [TASK ACTION] Failed:', error);
      toast.error('Failed to update task');
    }
  };

  // 🚀 PROACTIVE CHATBOT INTEGRATION: Convert tasks to WorkflowAlert format for chatbot
  const convertTasksToWorkflowAlerts = (urgentTasks: NextTask[]): WorkflowAlert[] => {
    return urgentTasks.map(task => {
      const urgencyText = task.is_overdue ? 
        `OVERDUE by ${Math.round(task.hours_until_due || 0)} hours` :
        `due in ${Math.round(task.hours_until_due || 0)} hours`;
        
      const contextualMessage = generateContextualPrompt(task, urgencyText);
      
      return {
        workflow_id: task.id,
        workflow_type: task.type || 'general',
        workflow_stage: task.status || 'pending',
        due_date: task.due_date || task.created_at,
        hours_until_due: task.hours_until_due || 0,
        priority: task.urgency || 'medium',
        status: task.status || 'active',
        alert_type: task.is_overdue ? 'overdue' : 'due_soon',
        message: contextualMessage,
        actions: [
          {
            action: 'complete_task',
            label: 'Complete',
            icon: '✅'
          },
          {
            action: 'snooze_task',
            label: 'Snooze 1hr',
            icon: '⏰'
          }
        ],
        metadata: {
          created_at: task.created_at,
          workflow_progress: task.status === 'completed' ? 100 : 0,
          template_name: `${task.type}_workflow`
        }
      };
    });
  };

  // Generate contextual prompts based on task type and urgency
  const generateContextualPrompt = (task: NextTask, urgencyText: string): string => {
    const taskType = task.type?.toLowerCase() || 'general';
    const taskName = task.description;
    
    // Intelligent prompt templates based on task type
    const promptTemplates: Record<string, string> = {
      'follow_up': `🔔 **Proactive Alert**: Your follow-up task "${taskName}" is ${urgencyText}. This is a critical client touchpoint that could impact your relationship. Would you like me to help you prepare talking points or draft a follow-up message?`,
      
      'consultation': `⚠️ **Urgent**: Your consultation "${taskName}" is ${urgencyText}. Preparation is key for successful consultations. Shall I help you review the client's profile, prepare market data, or create a consultation checklist?`,
      
      'documentation': `📋 **Task Alert**: Your documentation task "${taskName}" is ${urgencyText}. Missing paperwork can delay deals significantly. Do you need help organizing required documents or creating a completion timeline?`,
      
      'property_showing': `🏠 **Critical**: Your property showing "${taskName}" is ${urgencyText}. Client showings are high-value activities that require preparation. Would you like me to help with showing notes, comparable properties, or client questions preparation?`,
      
      'listing': `🎯 **High Priority**: Your listing task "${taskName}" is ${urgencyText}. Timely listing activities are crucial for market positioning. Shall I help with market analysis, pricing strategy, or listing optimization?`,
      
      'contract': `⚖️ **URGENT**: Your contract task "${taskName}" is ${urgencyText}. Contract deadlines are legally binding and missing them can have serious consequences. Do you need help reviewing terms, deadlines, or next steps?`,
      
      'closing': `🏁 **CRITICAL**: Your closing task "${taskName}" is ${urgencyText}. Closing activities are time-sensitive and impact your commission. Would you like me to help with closing checklists, document verification, or coordination?`,
      
      'general': `📋 **Task Reminder**: Your task "${taskName}" is ${urgencyText}. Staying on top of your tasks maintains momentum with clients. How can I help you complete this efficiently?`
    };
    
    // Find matching template or use general template
    const template = promptTemplates[taskType] || promptTemplates['general'];
    
    return template;
  };


  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <Card.Header className="bg-primary text-white">
          <FontAwesomeIcon icon={faBrain} className="me-2" />
          Universal Lead Evaluation
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0">Loading lead evaluation and next steps...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <Card.Header className="bg-primary text-white">
          <FontAwesomeIcon icon={faBrain} className="me-2" />
          Universal Lead Evaluation
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  // Force refresh function that clears cache
  const forceRefreshEvaluation = async () => {
    const cacheKey = `lead_evaluation_${leadId}`;
    const orchestratorCacheKey = `workflow_orchestrator_${leadId}`;
    
    // Clear caches
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem(orchestratorCacheKey);
    
    console.log('🔄 [FORCE REFRESH] Clearing cache and re-evaluating lead...');
    console.log('🔄 [FORCE REFRESH] Cache key:', cacheKey);
    
    // First, refresh the lead AI context to ensure we have latest data
    try {
      console.log('🔄 [CONTEXT REFRESH] Refreshing lead AI context first...');
      await axiosInstance.post(`/leads/${leadId}/context/refresh`);
      console.log('✅ [CONTEXT REFRESH] Context refreshed successfully');
    } catch (error: any) {
      console.warn('⚠️ [CONTEXT REFRESH] Could not refresh context:', error);
    }
    
    // Then reload data with fresh context - force refresh
    loadUniversalData(true);
  };

  return (
    <Card className={className}>
      <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
        <div>
          <FontAwesomeIcon icon={faBrain} className="me-2" />
          Universal Lead Evaluation
        </div>
        <button 
          className="btn btn-sm btn-light" 
          onClick={forceRefreshEvaluation}
          disabled={loading}
          title="Force re-evaluation (clears cache)"
        >
          <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
          Refresh
        </button>
      </Card.Header>

      <Card.Body>
        {/* Lead Score Section */}
        <div className="mb-4">
          <h6 className="fw-bold mb-3">
            <FontAwesomeIcon icon={faChartLine} className="me-2 text-success" />
            Lead Evaluation Score
          </h6>
          
          {leadScore ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Lead Score: <strong>{leadScore.score || 0}/100</strong></span>
                <Badge bg={getQualityColor(leadScore.quality_level)}>
                  {(leadScore.quality_level || 'pending').toUpperCase()}
                </Badge>
              </div>
              
              <ProgressBar 
                now={leadScore.score || 0} 
                variant={getQualityColor(leadScore.quality_level)}
                className="mb-2"
              />
              
              <div className="row text-center">
                <div className="col-4">
                  <small className="text-muted">Conversion</small>
                  <div className="fw-bold">{Math.round((leadScore.conversion_potential || 0) * 100)}%</div>
                </div>
                <div className="col-4">
                  <small className="text-muted">Lead Value</small>
                  <div className="fw-bold">${(leadScore.lead_value || 0).toLocaleString()}</div>
                </div>
                <div className="col-4">
                  <small className="text-muted">Updated</small>
                  <div className="fw-bold">{formatDate(leadScore.evaluation_date)}</div>
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              Universal evaluation pending. Scores will appear after lead processing.
            </Alert>
          )}
        </div>

        {/* Next Tasks Section */}
        <div>
          <h6 className="fw-bold mb-3">
            <FontAwesomeIcon icon={faTasks} className="me-2 text-primary" />
            Next Steps (LLM Scheduled)
          </h6>
          
          {nextTasks.length > 0 ? (
            <div className="space-y-3">
              {nextTasks.slice(0, 5).map((task, index) => (
                <div key={task.id} className={`border rounded p-3 mb-2 ${task.is_overdue ? 'border-danger' : task.urgency === 'high' ? 'border-warning' : ''}`}>
                  {/* Proactive Alert Header */}
                  {(task.is_overdue || task.urgency === 'high' || task.urgency === 'critical') && (
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2">{getUrgencyIcon(task.urgency, task.is_overdue)}</span>
                      <Badge bg={getUrgencyColor(task.urgency)} className="me-2">
                        {task.is_overdue ? 'OVERDUE' : task.urgency?.toUpperCase()}
                      </Badge>
                      {task.is_overdue && task.due_date && (
                        <small className="text-danger fw-bold">
                          Overdue by {Math.round(task.hours_until_due || 0)} hours
                        </small>
                      )}
                      {!task.is_overdue && task.urgency === 'high' && task.due_date && (
                        <small className="text-warning fw-bold">
                          Due in {Math.round(task.hours_until_due || 0)} hours
                        </small>
                      )}
                      {!task.due_date && task.urgency === 'medium' && (
                        <small className="text-info fw-bold">
                          Recent task - needs attention
                        </small>
                      )}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="fw-bold text-dark mb-1">
                        {task.description}
                      </div>
                      <div className="text-muted small">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Type: {task.type} • 
                        {task.due_date ? 
                          `Due: ${formatDate(task.due_date)}` : 
                          `Created: ${formatDate(task.created_at)}`
                        }
                      </div>
                    </div>
                    <div className="ms-2">
                      <Badge bg={getPriorityColor(task.priority)} className="mb-1">
                        Priority {task.priority}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Proactive One-Click Actions */}
                  {(task.is_overdue || task.urgency === 'high' || task.urgency === 'critical') && (
                    <div className="mt-2 d-flex gap-2">
                      <button 
                        className="btn btn-success btn-sm"
                        onClick={() => handleTaskAction(task.id, 'complete')}
                      >
                        ✅ Complete
                      </button>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleTaskAction(task.id, 'snooze')}
                      >
                        ⏰ Snooze 1hr
                      </button>
                    </div>
                  )}
                </div>
              ))}
              
              {nextTasks.length > 5 && (
                <div className="text-center mt-2">
                  <small className="text-muted">
                    ... and {nextTasks.length - 5} more tasks
                  </small>
                </div>
              )}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              No pending tasks. Workflow will generate next steps based on lead activity.
            </Alert>
          )}
        </div>
      </Card.Body>
      
      <Card.Footer className="text-muted text-center">
        <small>
          <FontAwesomeIcon icon={faBrain} className="me-1" />
          Powered by Universal Lead Evaluation System
        </small>
      </Card.Footer>
    </Card>
  );
};

export default UniversalWorkflowPanel;